import React, { useEffect, useRef, useState, useCallback } from "react";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { IoCamera, IoWarning, IoRefresh } from "react-icons/io5";

type QRScannerProps = {
  paused: boolean;
  onResult: (decodedText: string) => void;
  onError?: (err: any) => void;
};

const QRScanner: React.FC<QRScannerProps> = ({ paused, onResult, onError }) => {
  const scannerRef = useRef<HTMLDivElement>(null);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const cameraConfigRef = useRef<any>(null);
  const isUnmountedRef = useRef(false);
  const initializingRef = useRef(false);
  const [isCooldown, setIsCooldown] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [scannerState, setScannerState] = useState<
    "loading" | "ready" | "error" | "no-camera"
  >("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // تنظيف الكاميرا بشكل كامل
  const cleanupCamera = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (
          state === Html5QrcodeScannerState.SCANNING ||
          state === Html5QrcodeScannerState.PAUSED
        ) {
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
        html5QrCodeRef.current = null;
      } catch (err) {
        // Handle error silently
      }
    }
    initializingRef.current = false;
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const mobileRegex =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      const isMobileDevice =
        mobileRegex.test(userAgent) || window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // حساب حجم QR box بناءً على حجم الشاشة
  const getQRBoxSize = useCallback(() => {
    if (!scannerRef.current) return { width: 250, height: 250 };

    const containerWidth = scannerRef.current.offsetWidth;
    const containerHeight = scannerRef.current.offsetHeight;

    // حساب الحجم المناسب (70% من أصغر بُعد)
    const minDimension = Math.min(containerWidth, containerHeight);
    const qrSize = Math.floor(minDimension * 0.7);

    // حدود دنيا وعليا
    const minSize = isMobile ? 180 : 220;
    const maxSize = isMobile ? 280 : 350;

    const finalSize = Math.max(minSize, Math.min(maxSize, qrSize));

    return { width: finalSize, height: finalSize };
  }, [isMobile]);

  // تهيئة الكاميرا مع معالجة أفضل للأخطاء
  const initializeCamera = useCallback(async () => {
    if (isUnmountedRef.current || initializingRef.current) return;

    initializingRef.current = true;

    try {
      setScannerState("loading");
      setErrorMessage("");

      // تنظيف أي مسح سابق
      await cleanupCamera();

      // انتظار قصير لضمان تنظيف الكاميرا
      await new Promise((resolve) => setTimeout(resolve, 500));

      if (isUnmountedRef.current) return;

      // التحقق من دعم MediaDevices
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setScannerState("error");
        setErrorMessage("متصفحك لا يدعم استخدام الكاميرا");
        return;
      }

      // محاولة الحصول على إذن الكاميرا مع معاملات مبسطة
      let stream;
      try {
        const basicConstraints = isMobile
          ? {
              video: {
                facingMode: { ideal: "environment" },
                width: { ideal: 640 },
                height: { ideal: 640 },
              },
            }
          : { video: true };

        stream = await navigator.mediaDevices.getUserMedia(basicConstraints);

        // إيقاف الـ stream فوراً لأن Html5Qrcode سيدير الكاميرا
        stream.getTracks().forEach((track) => track.stop());
      } catch (permissionError) {
        setScannerState("error");
        setErrorMessage(
          isMobile
            ? "يرجى السماح للتطبيق بالوصول للكاميرا من إعدادات المتصفح"
            : "يرجى السماح للتطبيق بالوصول للكاميرا"
        );
        return;
      }

      if (isUnmountedRef.current) return;

      // الحصول على قائمة الكاميرات
      let cameras;
      try {
        cameras = await Html5Qrcode.getCameras();
      } catch (cameraError) {
        setScannerState("error");
        setErrorMessage("تعذر الوصول لقائمة الكاميرات");
        return;
      }

      if (cameras.length === 0) {
        setScannerState("no-camera");
        setErrorMessage("لا توجد كاميرا متاحة على هذا الجهاز");
        return;
      }

      // اختيار الكاميرا المناسبة مع fallback
      let selectedCamera = null;

      if (isMobile) {
        // للموبايل: ابحث عن الكاميرا الخلفية
        selectedCamera = cameras.find(
          (cam) =>
            cam.label.toLowerCase().includes("back") ||
            cam.label.toLowerCase().includes("rear") ||
            cam.label.toLowerCase().includes("environment")
        );

        // إذا لم توجد، استخدم آخر كاميرا (عادة الخلفية)
        if (!selectedCamera && cameras.length > 1) {
          selectedCamera = cameras[cameras.length - 1];
        }

        // إذا لم توجد، استخدم أول كاميرا متاحة
        if (!selectedCamera) {
          selectedCamera = cameras[0];
        }
      } else {
        // للديسكتوب: استخدم أول كاميرا متاحة
        selectedCamera = cameras[0];
      }

      // إعداد الكاميرا مع fallback
      if (selectedCamera) {
        cameraConfigRef.current = { deviceId: { exact: selectedCamera.id } };
      } else {
        cameraConfigRef.current = isMobile
          ? { facingMode: { ideal: "environment" } }
          : { facingMode: "user" };
      }

      if (!isUnmountedRef.current) {
        setScannerState("ready");
      }
    } catch (err: any) {
      if (!isUnmountedRef.current) {
        setScannerState("error");

        // رسائل خطأ مخصصة حسب نوع الخطأ
        let customMessage = "حدث خطأ في تشغيل الكاميرا";

        if (err.name === "NotReadableError") {
          customMessage = isMobile
            ? "الكاميرا مستخدمة من تطبيق آخر، أغلق التطبيقات الأخرى وحاول مرة أخرى"
            : "تعذر الوصول للكاميرا، تأكد من عدم استخدامها في تطبيق آخر";
        } else if (err.name === "NotAllowedError") {
          customMessage = "تم رفض إذن الكاميرا، يرجى السماح بالوصول للكاميرا";
        } else if (err.name === "NotFoundError") {
          customMessage = "لم يتم العثور على كاميرا متاحة";
        } else if (err.name === "NotSupportedError") {
          customMessage = "الكاميرا غير مدعومة في هذا المتصفح";
        }

        setErrorMessage(customMessage);
        onError?.(err);
      }
    } finally {
      initializingRef.current = false;
    }
  }, [isMobile, onError, cleanupCamera]);

  // بدء المسح مع معالجة محسنة
  const startScanner = useCallback(async () => {
    if (isUnmountedRef.current || !cameraConfigRef.current) return;

    try {
      // تنظيف المسح السابق إن وجد
      await cleanupCamera();

      // انتظار قصير
      await new Promise((resolve) => setTimeout(resolve, 200));

      if (isUnmountedRef.current) return;

      const html5QrCode = new Html5Qrcode("qr-scanner");
      html5QrCodeRef.current = html5QrCode;

      // حساب حجم QR box المناسب
      const qrBoxSize = getQRBoxSize();

      // إعدادات محسنة ومبسطة
      const qrConfig = {
        fps: isMobile ? 5 : 8,
        qrbox: qrBoxSize,
        aspectRatio: 1.0,
        disableFlip: false,
        videoConstraints: isMobile
          ? {
              width: { ideal: 640, max: 1280 },
              height: { ideal: 640, max: 1280 },
              facingMode: { ideal: "environment" },
            }
          : {
              width: { ideal: 1280, max: 1920 },
              height: { ideal: 720, max: 1080 },
            },
      };

      await html5QrCode.start(
        cameraConfigRef.current,
        qrConfig,
        (decodedText) => {
          if (!isCooldown && !isUnmountedRef.current) {
            onResult(decodedText);
            setIsCooldown(true);
            setTimeout(() => {
              if (!isUnmountedRef.current) {
                setIsCooldown(false);
              }
            }, 2000);
          }
        },
        (errorMessage) => {
          // تجاهل الأخطاء البسيطة
          if (!errorMessage.includes("NotFoundException")) {
            // Handle warning silently
          }
        }
      );
    } catch (err: any) {
      if (!isUnmountedRef.current) {
        setScannerState("error");

        let customMessage = "فشل في بدء المسح";

        if (err.name === "NotReadableError") {
          customMessage =
            "الكاميرا مستخدمة، أغلق التطبيقات الأخرى وحاول مرة أخرى";
        }

        setErrorMessage(customMessage);
        onError?.(err);
      }
    }
  }, [cleanupCamera, getQRBoxSize, isMobile, isCooldown, onResult, onError]);

  // التحكم في الإيقاف المؤقت
  useEffect(() => {
    const handlePauseResume = async () => {
      if (!html5QrCodeRef.current || isUnmountedRef.current) return;

      try {
        const state = html5QrCodeRef.current.getState();

        if (paused && state === Html5QrcodeScannerState.SCANNING) {
          await html5QrCodeRef.current.pause();
        } else if (!paused && state === Html5QrcodeScannerState.PAUSED) {
          await html5QrCodeRef.current.resume();
        }
      } catch (err) {
        // Handle error silently
      }
    };

    handlePauseResume();
  }, [paused]);

  // تهيئة المسح الأولي مع debouncing
  useEffect(() => {
    isUnmountedRef.current = false;

    // تأخير قصير لتجنب multiple calls
    const timeoutId = setTimeout(() => {
      if (!isUnmountedRef.current) {
        initializeCamera();
      }
    }, 100);

    // تنظيف عند إلغاء المكون
    return () => {
      clearTimeout(timeoutId);
      isUnmountedRef.current = true;
      cleanupCamera();
    };
  }, [initializeCamera, cleanupCamera]);

  // بدء المسح عند الاستعداد مع debouncing
  useEffect(() => {
    if (scannerState === "ready" && !isUnmountedRef.current) {
      const timeoutId = setTimeout(() => {
        if (!isUnmountedRef.current && scannerState === "ready") {
          startScanner();
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [scannerState, startScanner]);

  // إعادة المحاولة مع تأخير
  const handleRetry = useCallback(() => {
    if (!isUnmountedRef.current && !initializingRef.current) {
      setTimeout(() => {
        if (!isUnmountedRef.current) {
          initializeCamera();
        }
      }, 1000);
    }
  }, [initializeCamera]);

  // رسائل الحالة
  const renderStateMessage = () => {
    switch (scannerState) {
      case "loading":
        return (
          <div className='absolute inset-0 flex flex-col items-center justify-center bg-gray-100 text-gray-600 z-10'>
            <IoCamera className='text-4xl mb-4 animate-pulse' />
            <p className='text-lg font-medium'>جاري تحضير الكاميرا...</p>
          </div>
        );

      case "error":
        return (
          <div className='absolute inset-0 flex flex-col items-center justify-center bg-gray-100 text-red-600 z-10 p-4'>
            <IoWarning className='text-4xl mb-4' />
            <p className='text-center text-sm mb-4'>{errorMessage}</p>
            <button
              onClick={handleRetry}
              className='flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors'
            >
              <IoRefresh />
              إعادة المحاولة
            </button>
          </div>
        );

      case "no-camera":
        return (
          <div className='absolute inset-0 flex flex-col items-center justify-center bg-gray-100 text-gray-600 z-10 p-4'>
            <IoWarning className='text-4xl mb-4' />
            <p className='text-center text-sm'>{errorMessage}</p>
          </div>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        // عند إخفاء الصفحة تماماً أوقف الكاميرا نهائياً لتوفير البطارية والأمان
        cleanupCamera();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    const handlePageHide = () => {
      cleanupCamera();
    };

    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handlePageHide);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handlePageHide);
    };
  }, [cleanupCamera]);

  return (
    <div className='w-full h-full relative'>
      <div
        id='qr-scanner'
        ref={scannerRef}
        className='w-full h-full'
        style={{ position: "relative" }}
      />
      {renderStateMessage()}
    </div>
  );
};

export default QRScanner;
